<template>
  <div v-if="isHeadCounterOrManager">
    <b-button variant="primary" size="sm" :disabled="isBusy" @click="openImport">
      <b-spinner v-if="isBusy" small class="mr-1" />
      <i v-else class="fas fa-file-import mr-1"></i>
      {{ isBusy ? copy.busy : copy.button }}
    </b-button>

    <b-modal ref="importModal" :title="copy.title" hide-footer>
      <p class="mb-2">{{ copy.what }}</p>
      <ol class="import-steps small text-muted">
        <li v-for="(step, i) in copy.steps" :key="i">{{ step }}</li>
      </ol>
      <div v-if="quotaStatus" class="ocr-quota small mb-2" :class="{ exhausted: quotaStatus.exhausted }">
        <i class="fas fa-robot mr-1"></i>{{ quotaStatus.text }}
        <template v-if="quotaStatus.exhausted">
          · <router-link :to="{ name: 'BillingPlans', params: $route.params }">ดูแพ็กเพื่ออ่านเพิ่ม</router-link>
          <div class="text-muted">ระหว่างนี้ทำเครื่องหมายรับแล้วเองได้จากแท็บค้างรับ</div>
        </template>
      </div>
      <b-form-file
        ref="fileInput"
        v-model="file"
        :accept="copy.accept"
        placeholder="ลากไฟล์มาวางที่นี่ หรือกดเลือกไฟล์"
        browse-text="เลือกไฟล์"
        drop-placeholder="วางไฟล์ที่นี่..."
        :disabled="isBusy || (quotaStatus && quotaStatus.exhausted)"
        @input="onFileSelected"
      />
      <small class="text-muted d-block mt-1">รองรับไฟล์ {{ copy.types }}</small>
    </b-modal>

    <OcrReviewModal ref="ocrReviewModal" :payer="payer" @committed="onImported" />
    <Dialog ref="Dialog" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import OcrReviewModal from "@/components/reconciliation/OcrReviewModal.vue";
import Dialog from "@/components/modal/Dialog.vue";
import {
  apiErrorMessage, duplicateBatchOf, duplicateImportHint, ocrQuotaStatus, replaceDuplicateMessage, scanPreviewKind,
} from "@/utils/reconciliation";
import { MAX_UPLOAD_BYTES, renderPdfPages, shrinkImage } from "@/utils/pdfPages";

// What each payer's import is for, in the words of the documents staff actually receive.
const IMPORT_COPY = {
  SSO: {
    button: "นำเข้าใบคำสั่งจ่าย ปกส.",
    busy: "กำลังอ่านเอกสาร...",
    title: "นำเข้าใบคำสั่งจ่ายจากประกันสังคม",
    what:
      "ใช้เมื่อได้รับเงินจากสำนักงานประกันสังคมพร้อมเอกสาร “คำสั่งประโยชน์ทดแทนและใบคำสั่งจ่าย” ระบบจะอ่านเลขบัตร ปชช. วันที่ใช้สิทธิ และจำนวนเงินจากเอกสาร แล้วจับคู่กับรายการค้างรับให้",
    steps: [
      "สแกนหรือถ่ายรูปเอกสารให้เห็นตารางครบทุกแถว (PDF หลายหน้าได้)",
      "ตรวจและแก้ข้อมูลที่ระบบอ่านได้ก่อนกดบันทึก",
      "รายการที่จับคู่ได้ย้ายไป “รับแล้ว” ที่เหลืออยู่ใน “รอตรวจสอบ”",
    ],
    accept: "application/pdf,image/jpeg,image/png",
    types: "PDF, JPG หรือ PNG",
    failed: "อ่านข้อมูลจากเอกสารไม่สำเร็จ กรุณาลองใหม่",
  },
  NHSO: {
    button: "นำเข้าไฟล์ REP สปสช.",
    busy: "กำลังนำเข้า...",
    title: "นำเข้าไฟล์ REP จาก สปสช.",
    what:
      "ใช้เมื่อ สปสช. โอนเงินชดเชยแล้ว ดาวน์โหลดไฟล์ REP ของงวดนั้นจากระบบ e-Claim แล้วนำเข้าที่นี่ ระบบจะจับคู่ visit ที่ได้รับชดเชยด้วยเลขบัตร ปชช. และวันที่ใช้สิทธิให้",
    steps: [
      "ดาวน์โหลดไฟล์ REP (Excel) จาก e-Claim สปสช.",
      "เลือกไฟล์ด้านล่าง ระบบนำเข้าทันที (เฉพาะรายการที่ “ชดเชย”)",
      "visit ที่จับคู่ได้ย้ายไป “รับแล้ว” ที่เหลืออยู่ใน “รอตรวจสอบ”",
    ],
    accept: ".xlsx,.xls,.csv",
    types: "Excel (.xlsx)",
    failed: "นำเข้าไฟล์ไม่สำเร็จ กรุณาลองใหม่",
  },
};

export default {
  name: "ImportControls",
  components: { OcrReviewModal, Dialog },
  props: {
    payer: { type: String, required: true },
  },
  data() {
    return {
      file: null,
      isBusy: false,
      // SSO only: this month's AI reads { used, limit } (the NHSO REP import uses no AI)
      ocrQuota: null,
    };
  },
  computed: {
    ...mapGetters({ isAccessMenu: "moduleUser/getIsAccessMenu" }),
    // reconciliation.manage (upload/OCR import) is MANAGER/HEADCOUNTER only on
    // the backend; COUNTER only has reconciliation.view. Gate both upload
    // controls here so a COUNTER sees a read-only page instead of an input
    // whose submit 403s.
    isHeadCounterOrManager() {
      return this.isAccessMenu(["MANAGER", "HEADCOUNTER"]);
    },
    copy() {
      return IMPORT_COPY[this.payer];
    },
    quotaStatus() {
      return this.payer === "SSO" ? ocrQuotaStatus(this.ocrQuota) : null;
    },
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
  },
  methods: {
    ...mapActions("moduleReconciliation", ["ocrExtract", "uploadStatement", "fetchOcrQuota", "undoBatch"]),
    openImport() {
      this.$refs.importModal.show();
      if (this.payer === "SSO") this.loadOcrQuota();
    },
    async loadOcrQuota() {
      try {
        this.ocrQuota = await this.fetchOcrQuota({ clinicUrl: this.clinicUrl, branchUrl: this.branchUrl });
      } catch {
        this.ocrQuota = null; // no line rather than a wrong one; the read itself still checks the quota
      }
    },
    async onFileSelected(file) {
      if (!file) return;
      this.$refs.importModal.hide();
      this.isBusy = true;
      try {
        if (this.payer === "SSO") await this.readSsoStatement(file);
        else await this.importNhsoFile(file);
      } catch (err) {
        const duplicate = duplicateBatchOf(err);
        if (duplicate) await this.replaceDuplicate(duplicate, () => this.importNhsoFile(file));
        else this.$refs.Dialog.showToast("danger", duplicateImportHint(apiErrorMessage(err, this.copy.failed)));
      } finally {
        this.isBusy = false;
        this.file = null;
        if (this.$refs.fileInput) this.$refs.fileInput.reset();
      }
    },
    async readSsoStatement(file) {
      const formData = new FormData();
      const kind = scanPreviewKind(file);
      // a large photo is shrunk first: the API takes about 6 MB per request
      const upload = kind === "image" ? await shrinkImage(file) : file;
      formData.append("file", upload, upload === file ? file.name : file.name.replace(/\.\w+$/, ".jpg"));
      // A PDF is read as page images (see pdfPages.js); the same images are the preview.
      let previews;
      if (kind === "pdf") {
        let pages;
        try {
          pages = await renderPdfPages(file);
        } catch {
          this.$refs.Dialog.showToast("danger", "เปิดไฟล์ PDF นี้ไม่ได้ ลองบันทึกเป็นภาพ JPG หรือ PNG แล้วเลือกใหม่");
          return;
        }
        const bytes = file.size + pages.reduce((sum, p) => sum + p.blob.size, 0);
        if (bytes > MAX_UPLOAD_BYTES) {
          pages.forEach((p) => URL.revokeObjectURL(p.url));
          this.$refs.Dialog.showToast("danger", `เอกสาร ${pages.length} หน้านี้ใหญ่เกินไป ลองแยกเป็นหลายไฟล์ที่มีหน้าน้อยลง`);
          return;
        }
        pages.forEach((p, i) => formData.append("pages", p.blob, `page-${i + 1}.${p.blob.type === "image/png" ? "png" : "jpg"}`));
        previews = pages.map((p) => p.url);
      } else {
        previews = [URL.createObjectURL(upload)];
      }
      try {
        const res = await this.ocrExtract({ clinicUrl: this.clinicUrl, branchUrl: this.branchUrl, formData });
        // Nothing is persisted by ocr-extract — hand the extracted rows to the review modal for staff
        // correction; commitReviewed is the only write. The modal now owns (and revokes) the previews.
        if (res.data.quota) this.ocrQuota = res.data.quota;
        this.$refs.ocrReviewModal.open(res.data, previews);
      } catch (err) {
        previews.forEach((url) => URL.revokeObjectURL(url));
        if (err?.response?.status === 403) this.loadOcrQuota(); // used up meanwhile: show it in the dialog
        throw err;
      }
    },
    async importNhsoFile(file) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("payer", this.payer);
      // statementRef/depositAmount/statementDate are optional here — the REP workbook itself carries
      // them and the backend falls back to the file's own values when they're omitted.
      const res = await this.uploadStatement({ clinicUrl: this.clinicUrl, branchUrl: this.branchUrl, formData });
      this.$emit("imported", res.data);
    },
    // The statement was imported before: offer to undo that batch and import this file in its place.
    async replaceDuplicate(duplicate, importAgain) {
      const confirmed = await this.$bvModal.msgBoxConfirm(replaceDuplicateMessage(duplicate), {
        title: "เอกสารนี้ถูกนำเข้าไปแล้ว", okVariant: "danger", okTitle: "ยกเลิกชุดเดิมแล้วนำเข้าใหม่", cancelTitle: "ปิด",
      });
      if (!confirmed) return;
      try {
        await this.undoBatch({ clinicUrl: this.clinicUrl, branchUrl: this.branchUrl, batchId: duplicate.batchId });
        await importAgain();
      } catch (err) {
        this.$refs.Dialog.showToast("danger", apiErrorMessage(err, this.copy.failed));
      }
    },
    onImported(result) {
      this.$emit("imported", result);
    },
  },
};
</script>

<style scoped>
.ocr-quota {
  color: #1a4ec0;
}
.ocr-quota.exhausted {
  color: #8a5300;
}
.import-steps {
  padding-left: 1.2rem;
  margin-bottom: 12px;
}
</style>
